// Import the file directly, not the "." barrel — the barrel also exports
// Navbar, whose import of this file makes the cycle barrel → Navbar →
// Notification → barrel and crashes module init (TDZ on default exports).
import Button from "./Button";
import { MdOutlineCancel } from "react-icons/md";
import { useStateContext } from "../contexts/ContextProvider";
import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { formatDubaiTime } from "../utils/formatDubaiTime";

const Notification = ({
  notifications,
  unreadCount,
  markAsRead,
  setShowCloseButton,
}) => {
  const { setIsClicked, initialState } = useStateContext();
  const notificationRef = useRef(null);
  const navigate = useNavigate();

  const handleClose = () => {
    setIsClicked(initialState);
    setShowCloseButton(false);
  };

  // Function to handle notification click and navigation
  const handleNotificationClick = async (item) => {

    // Mark as read if unread
    if (item.seen === 0) {
      try {
        await markAsRead(item.id);
      } catch (error) {
        console.error("Error marking as read:", error);
      }
    }

    // Handle navigation based on click_action
    if (item.click_action) {
      const route = getRouteFromAction(item.click_action, item);
      if (route) {
        navigate(route);
        handleClose(); 
      }
    }
    // If click_action is null, do nothing (just view notification)
  };

  // Function to determine route based on click_action
  const getRouteFromAction = (action, item) => {
    switch (action) {
      case "OPEN_BID_DETAILS":
      case "OPEN_JOB_QUOTE_DETAILS":
      case "JOB_CANCELLED":
      case "JOB_ASSIGNED":
      case "JOB_STARTED":
      case "OPEN_JOB_DETAILS":
      case "JOB_COMPLETED":
      case "NEW_JOB_OFFER":
        return item.job?.id ? `/jobs/${item.job.id}/detail` : "/jobs";

      case "DISPUTE_CREATED":
        return "/disputes";

      case "REFERRAL_BONUS":
        return "refer-friend-list";

      case "USER_REGISTERED":
        return "/dashboard";

      case "COMPANY_STATUS_CHANGED":
        return "/companies";

      case "WITHDRAWAL_REQUEST":
        return "/withdrawal-requests";

      case "CUSTOMER_STATUS_CHANGED":
        return "/customers";

      case "GEEK_STATUS_CHANGED":
        return item.geek?.id ? `/geeks/${item.geek.id}/detail` : "/geeks";

      case "OPEN_GEEK_PROFILE":
        return item.geek?.id ? `/geeks/${item.geek.id}/detail` : "/geeks";

      case "OPEN_MESSAGE_ACTIVITY":
        return "/chat";

      case "PROFILE_UPDATED":
        return "/dashboard";

      default:
        return null;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        handleClose && handleClose(); // Call the provided onClose function
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClose]);

  const formatDate = (dateStr) =>
    formatDubaiTime(dateStr, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      ref={notificationRef}
      className="nav-item absolute right-5 md:right-40 top-16 bg-white dark:bg-[#1F2937] p-4 rounded-2xl w-96 shadow-2xl z-50 border border-gray-100/10 dark:border-gray-700/30 backdrop-blur-sm"
      style={{
        borderColor: "#fba86f",
        borderWidth: "2px",
        boxShadow: "0 25px 50px -12px rgba(251, 168, 111, 0.25)",
      }}
    >
      <div
        className="flex justify-between items-center border-b border-gray-100 dark:border-gray-700/50 pb-2 mb-2"
        style={{ borderColor: "#fba86f" }}
      >
        <div className="flex items-center gap-2">
          <p
            className="font-bold text-base dark:text-gray-100"
            style={{ color: "#fba86f" }}
          >
            Notifications
          </p>
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-white text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ backgroundColor: "#fba86f" }}
          >
            {unreadCount}
          </motion.span>
        </div>
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
          <Button
            icon={<MdOutlineCancel size={18} />}
            bgHoverColor="light-gray"
            size="md"
            styles={{
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              backgroundColor: "rgba(251, 168, 111, 0.1)",
              color: "#fba86f",
            }}
            borderRadius="25px"
            onClick={handleClose}
          />
        </motion.div>
      </div>

      <div className="max-h-[280px] overflow-y-auto pr-1 space-y-2 custom-scrollbar">
        <AnimatePresence>
          {notifications.length ? (
            notifications.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                whileHover={{ scale: 1.02, translateX: 4 }}
                transition={{ duration: 0.2 }}
                className={`group relative overflow-hidden shadow-md border dark:border-gray-700/30 rounded-lg p-3 hover:shadow-lg transition-all ${
                  item.click_action ? "cursor-pointer" : "cursor-default"
                } ${
                  item.seen === 1
                    ? "bg-gray-50/50 dark:bg-gray-800/30"
                    : "bg-white dark:bg-gray-800/50"
                }`}
                style={{
                  borderWidth: "2px",
                  borderColor: item.seen === 0 ? "#fba86f" : "gray",
                  boxShadow:
                    item.seen === 0
                      ? "0 4px 6px -1px rgba(251, 168, 111, 0.1)"
                      : "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                }}
                onClick={() => handleNotificationClick(item)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p
                        className="font-semibold text-sm text-gray-900 dark:text-white leading-snug transition-colors"
                        style={{
                          color: item.seen === 0 ? "#fba86f" : "inherit",
                          "--tw-text-opacity":
                            item.seen === 0 ? "1" : "inherit",
                        }}
                      >
                        {item.subject}
                      </p>
                      {/* Show clickable indicator if click_action exists */}
                      {item.click_action && (
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-xs px-1.5 py-0.5 rounded text-white font-medium"
                          style={{ backgroundColor: "#fba86f" }}
                        >
                          {item.click_action.replace(/_/g, " ")}
                        </motion.span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 break-words leading-relaxed line-clamp-2 group-hover:line-clamp-none transition-all">
                      {item.message}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                        {item.user?.first_name} {item.user?.last_name}
                      </span>
                      <span className="text-gray-300 dark:text-gray-600">
                        •
                      </span>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        {formatDate(item.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
                {item.seen === 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-3 right-3 w-2 h-2 rounded-full ring-2"
                    style={{
                      backgroundColor: "#fba86f",
                      ringColor: "rgba(251, 168, 111, 0.2)",
                    }}
                  />
                )}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background:
                      "linear-gradient(to right, rgba(251, 168, 111, 0), rgba(251, 168, 111, 0.05))",
                  }}
                />
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-6"
            >
              <p
                className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-1"
                style={{ color: "#fba86f" }}
              >
                No notifications
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                You're all caught up!
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default Notification;
